import env from 'react-dotenv';
import axios from 'axios';
import tk from './TokenKeeper';
import { logError } from './logError';
import { toast } from 'react-hot-toast';

class Api {
    constructor() {
        this.retryToastId = null;
        this.failedRequestsCount = 0;
        this.isShowingRetryMessage = false;
        this.pendingRequests = new Set();
        this.pokeDebounceTimer = null;
        this.pokeRequestCache = new Map();
        this.hasShownRestoredMessage = false;
    }

    getToken() {
        return tk.getToken();
    }

    getAuthHeader() {
        const token = this.getToken();
        if (token) {
            return { Authorization: `Token ${token}` };
        }
    }

    getCancelToken(cancelToken) {
        if (cancelToken) {
            return { cancelToken: cancelToken };
        }
    }

    isNetworkError(error) {
        // Detect various types of network/connection errors
        return (
            !error.response || // No response received
            error.code === 'NETWORK_ERROR' ||
            error.code === 'ECONNABORTED' ||
            error.code === 'ECONNREFUSED' ||
            error.code === 'ENOTFOUND' ||
            error.code === 'ETIMEDOUT' ||
            (error.response?.status >= 500 && error.response?.status < 600) ||
            error.message?.toLowerCase().includes('network error') ||
            error.message?.toLowerCase().includes('failed to fetch')
        );
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    showGlobalRetryNotification() {
        if (!this.isShowingRetryMessage) {
            this.isShowingRetryMessage = true;
            this.hasShownRestoredMessage = false; // Reset the restored message flag when we start showing retry
            this.retryToastId = toast.loading('Nie mogę nawiązać połączenia z serwerem. Poczekaj chwilę...', {
                duration: Infinity, // Show until manually dismissed
                position: 'top-center',
                icon: '🔄',
            });
        }
    }

    hideGlobalRetryNotification() {
        if (this.isShowingRetryMessage && this.retryToastId) {
            toast.dismiss(this.retryToastId);
            this.isShowingRetryMessage = false;
            this.retryToastId = null;
        }
    }

    showConnectionRestoredNotification() {
        // Only show restored message once per retry cycle and only if we were showing retry message
        if (this.isShowingRetryMessage && !this.hasShownRestoredMessage) {
            this.hasShownRestoredMessage = true;
            this.hideGlobalRetryNotification();
            toast.success('Połączenie z serwerem zostało przywrócone!', {
                duration: 3000,
                position: 'top-center',
            });
        }
    }

    async retryWithInfiniteBackoff(apiCall, initialDelay = 1000, maxDelay = 8000) {
        const requestId = Math.random().toString(36).substring(7);
        this.pendingRequests.add(requestId);
        
        let attempt = 1;
        let hadNetworkError = false;

        while (true) {
            try {
                const result = await apiCall();
                
                // Success - cleanup and notify if we had previous failures
                this.pendingRequests.delete(requestId);
                
                if (hadNetworkError) {
                    this.failedRequestsCount = Math.max(0, this.failedRequestsCount - 1);
                    // Only show restored message when ALL failed requests are resolved
                    if (this.failedRequestsCount === 0) {
                        this.showConnectionRestoredNotification();
                    }
                }
                
                return result;
            } catch (error) {
                // If it's not a network error, throw immediately
                if (!this.isNetworkError(error)) {
                    this.pendingRequests.delete(requestId);
                    throw error;
                }
                
                // First network error for this request
                if (!hadNetworkError) {
                    hadNetworkError = true;
                    this.failedRequestsCount++;
                    this.showGlobalRetryNotification();
                }
                
                // Calculate delay: 1s, 2s, 4s, 8s, then cap at 8s
                const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
                await this.delay(delay);
                attempt++;
            }
        }
    }

    // Debounced poke method to prevent excessive /poke requests
    async pokeDebounced(path, payload, options = {}, debounceDelay = 500) {
        const cacheKey = `${path}-${JSON.stringify(payload)}`;
        
        // Cancel previous timer for this specific request
        if (this.pokeDebounceTimer) {
            clearTimeout(this.pokeDebounceTimer);
        }
        
        // Return existing promise if same request is already cached
        if (this.pokeRequestCache.has(cacheKey)) {
            return this.pokeRequestCache.get(cacheKey);
        }
        
        // Create promise for this request
        const requestPromise = new Promise((resolve, reject) => {
            this.pokeDebounceTimer = setTimeout(async () => {
                try {
                    const result = await this.post(path, payload, options);
                    this.pokeRequestCache.delete(cacheKey);
                    resolve(result);
                } catch (error) {
                    this.pokeRequestCache.delete(cacheKey);
                    reject(error);
                }
            }, debounceDelay);
        });
        
        this.pokeRequestCache.set(cacheKey, requestPromise);
        return requestPromise;
    }

    async get(path, cancelToken, options = {}) {
        return this.retryWithInfiniteBackoff(async () => {
            try {
                const { data } = await axios.get(env.API_URL + path, {
                    headers: {
                        ...this.getAuthHeader(),
                    },
                    ...this.getCancelToken(cancelToken),
                    ...options,
                });
                return data;
            } catch (error) {
                this.logAxiosError(error, 'GET', path, null, options);
                throw error;
            }
        });
    }

    async put(path, payload) {
        return this.retryWithInfiniteBackoff(async () => {
            try {
                const { data } = await axios.put(env.API_URL + path, payload, {
                    headers: {
                        ...this.getAuthHeader(),
                    },
                });
                return data;
            } catch (error) {
                this.logAxiosError(error, 'PUT', path, payload);
                throw error;
            }
        });
    }

    async patch(path, payload, options = {}) {
        return this.retryWithInfiniteBackoff(async () => {
            // Inner retry for database locked errors (legacy support)
            const retries = 3;
            const delay = 1000;
            for (let i = 0; i < retries; i++) {
                try {
                    const { data } = await axios.patch(env.API_URL + path, payload, {
                        headers: {
                            ...this.getAuthHeader(),
                        },
                        ...options,
                    });
                    return data;
                } catch (error) {
                    if (error.response && error.response.status === 500 && 
                        error.response.data?.includes && error.response.data.includes('database is locked')) {
                        if (i < retries - 1) {
                            await new Promise(resolve => setTimeout(resolve, delay));
                            continue;
                        } else {
                            const dbError = new Error('Database is locked after several retries');
                            this.logAxiosError(dbError, 'PATCH', path, payload, options);
                            throw dbError;
                        }
                    } else {
                        this.logAxiosError(error, 'PATCH', path, payload, options);
                        throw error;
                    }
                }
            }
        });
    }

    async post(path, payload, config) {
        // Special handling for /poke requests - use debounced version
        if (path.includes('/poke')) {
            return this.pokeDebounced(path, payload, config);
        }
        
        return this.retryWithInfiniteBackoff(async () => {
            try {
                const { data } = await axios.post(env.API_URL + path, payload, {
                    headers: {
                        ...this.getAuthHeader(),
                    },
                    ...config,
                });
                return data;
            } catch (error) {
                this.logAxiosError(error, 'POST', path, payload, config);
                throw error;
            }
        });
    }

    async delete(path) {
        return this.retryWithInfiniteBackoff(async () => {
            try {
                const { data } = await axios.delete(env.API_URL + path, {
                    headers: {
                        ...this.getAuthHeader(),
                    },
                });
                return data;
            } catch (error) {
                this.logAxiosError(error, 'DELETE', path);
                throw error;
            }
        });
    }

    // Public method to reset retry state (useful for debugging)
    resetRetryState() {
        this.hideGlobalRetryNotification();
        this.failedRequestsCount = 0;
        this.pendingRequests.clear();
        this.hasShownRestoredMessage = false;
        if (this.pokeDebounceTimer) {
            clearTimeout(this.pokeDebounceTimer);
            this.pokeDebounceTimer = null;
        }
        this.pokeRequestCache.clear();
    }

    logAxiosError(error, method, path, payload = null, config = null) {
        const errorInfo = {
            message: error.message,
            method,
            endpoint: env.API_URL + path,
            path,
            hasToken: !!this.getToken(),
            tokenPreview: this.getToken() ? this.getToken().slice(0, 5) + '...' : null,
            timestamp: new Date().toISOString(),
        };

        // Add payload info if present (but don't log sensitive data)
        if (payload) {
            errorInfo.payloadKeys = Object.keys(payload);
            errorInfo.payloadSize = JSON.stringify(payload).length;
        }

        // Add config info if present
        if (config) {
            errorInfo.configKeys = Object.keys(config);
        }

        // Add axios-specific error details
        if (error.response) {
            errorInfo.status = error.response.status;
            errorInfo.statusText = error.response.statusText;
            errorInfo.responseHeaders = error.response.headers;
        } else if (error.request) {
            errorInfo.requestError = 'No response received';
            errorInfo.requestReadyState = error.request.readyState;
            errorInfo.requestStatus = error.request.status;
        }

        // Add network error details
        if (error.code) {
            errorInfo.code = error.code;
        }

        // Create enhanced error for Sentry
        const enhancedError = new Error(`Axios ${method} Error: ${error.message}`);
        enhancedError.name = 'AxiosError';
        enhancedError.originalError = error;
        enhancedError.apiContext = errorInfo;

        console.error('API Error Details:', errorInfo);
        logError(enhancedError);
    }
}

export default new Api();
